/**
 * The shared refusal seam (mission 9 WP11 T069; FR-015; amendments 7, 8; squad U11, U17, U30;
 * contracts/ui-api.md "Spine").
 *
 * `useBillingGate()` answers whether a creating, spending or sending control is refused, and why,
 * from `creditsQuery().data?.billing`. There is no new component (U11): callers pass
 * `disabled={gate.refused}` and `disabledReason={gate.reason}` to the existing `Button`
 * (`components/ui/button.tsx`), which renders the reason in its tooltip and `aria-describedby`.
 * - `read_only` is refused with the reason by cause: `disabledReadOnlyDunning` for a failed payment,
 *   `disabledReadOnlyCancelled` otherwise (an ended plan);
 * - `pending_subscription` is refused with `disabledPending`;
 * - `active` and `dunning` are not refused: dunning keeps full access until read-only (B.1);
 * - an absent member, a mission 3 body or a read still loading, reads as `active` (U17). The server
 *   is the control; this only saves the reporter a refused click.
 *
 * `Button`'s props are a union (`disabled: true` requires a `disabledReason`), so the two members
 * written separately (`disabled={gate.refused} disabledReason={gate.reason}`) do not type-check;
 * `gateButton(gate)` is the same pair as one spreadable value: `<Button {...gateButton(gate)}>`.
 */
import { useQuery } from '@tanstack/react-query'

import type { BillingAccess } from './api/billing'
import { copyText } from './provisional-copy'
import { creditsQuery } from './queries'

export type BillingGate = { refused: false; reason: undefined } | { refused: true; reason: string }

/** The gate as `Button` props: nothing when open, `disabled` with its reason when refused. */
export type GateButtonProps = { disabled?: false } | { disabled: true; disabledReason: string }

const OPEN: BillingGate = { refused: false, reason: undefined }

/** The gate for one access member; `undefined` (absent) is active (U17). */
export function billingGate(access: BillingAccess | undefined): BillingGate {
  if (access?.state === 'pending_subscription') return { refused: true, reason: copyText('disabledPending') }
  if (access?.state === 'read_only') {
    const key = access.reason === 'dunning' ? 'disabledReadOnlyDunning' : 'disabledReadOnlyCancelled'
    return { refused: true, reason: copyText(key) }
  }
  return OPEN
}

/** The gate from the credits read's `billing` member (one observer of `['credits']`; no extra read). */
export function useBillingGate(): BillingGate {
  const { data } = useQuery(creditsQuery())
  return billingGate(data?.billing)
}

/** `<Button {...gateButton(gate)}>`: the existing Button's `disabled` and `disabledReason` (U11). */
export function gateButton(gate: BillingGate): GateButtonProps {
  return gate.refused ? { disabled: true, disabledReason: gate.reason } : {}
}
